import graphql from 'api/graphql'
import { oauthTokenSelector } from 'selectors/app'
import { BootstrapLoader } from 'util/bootstrap'
import { selectSearchByQueryId } from 'ducks/searches/selectors'
import { createDoFetchInMemoryByQueryId } from 'ducks/searches/operations/createDoFetchInMemoryByQueryId'
import { selectCurrentUser } from 'ducks/currentUser/selectors/base'
import { collectionQuery as collectionQueryV1 } from 'api/folders'
import { foldersByIdSelector } from 'selectors/folders_base'

import { doGraphqlRequest, doApiWriteRequest } from 'ducks/requests/operations'
import {
  FETCH_FOLDERS_REQUEST,
  FETCH_FOLDERS_SUCCESS,
  FETCH_FOLDERS_FAIL,
  SET_CURRENT_FOLDER,
  FETCH_FOLDERS,
  FETCH_FOLDERS_V2,
  DELETE_FOLDER,
} from '../actionTypes/folders'
import { dataTableQuery } from '../queries/folders/queries.v2'
import {
  folderGraphQlResponseSchema,
  folderGraphQlV2ResponseSchema,
} from '../schema/folders'

export function doSetCurrentFolder({ folderId }) {
  return {
    type: SET_CURRENT_FOLDER,
    payload: {
      folderId,
    },
  }
}

function transformAttachCurrentUser(data, { getState }) {
  const currentUser = selectCurrentUser(getState())
  return {
    ...data,
    currentUser,
  }
}

function calculateFolderAccess(data) {
  const {
    agents: { nodes: agents },
    channels: { nodes: channels },
    teams: { nodes: teams },
    folders: { nodes: folders },
  } = data
  const totalAgents = agents.length
  const totalChannels = channels.length
  const totalTeams = teams.length

  folders.forEach(folder => {
    const {
      channels: { nodes: folderChannels },
      teams: { nodes: folderTeams },
      agents: { nodes: folderAgents },
    } = folder

    const linkType =
      folderTeams.length > 0 && folderAgents.length === 0 ? 'team' : 'agent'

    const linkedAllAgents = totalAgents === folderAgents.length
    const linkedAllTeams = totalTeams === folderTeams.length
    let viewAccess
    if (linkType === 'team') {
      viewAccess = linkedAllTeams ? 'all-teams' : 'selected-teams'
    } else {
      viewAccess = linkedAllAgents ? 'all-agents' : 'selected-agents'
    }

    Object.assign(folder, {
      channelVisibility:
        totalChannels === folderChannels.length ? 'all' : 'selected',
      viewAccess,
    })
  })
  return data
}

const LOAD_ALL_FOLDERS_QUERYID = 'entityType:folder pageSize:10000'
export const doFetchFolders = ({ skipLoaded }) => (dispatch, getState) => {
  const queryId = LOAD_ALL_FOLDERS_QUERYID
  const cursor = 'all'
  const state = getState()
  const { loaded = null, cursors = {} } = selectSearchByQueryId(state, queryId)
  const hasCurrentPage = !!cursors[cursor]

  const hasLoaded = loaded && hasCurrentPage

  // Old way of loading folders
  // if (skipLoaded && folders && !isEmpty(folders)) {
  //   return Promise.resolve(folders)
  // }

  if (hasLoaded && skipLoaded) {
    const folders = foldersByIdSelector(state)
    // Note we might need to change this in future to return the results
    // from the previous query
    return Promise.resolve(folders)
  }

  return dispatch(
    doGraphqlRequest(
      FETCH_FOLDERS,
      collectionQueryV1(),
      {},
      {
        normalizationSchema: folderGraphQlResponseSchema,
        searches: {
          queryId,
          cursor,
          extractPagination: 'apiv1',
        },
        includeLegacyPayload: true,
        transformResponse: transformAttachCurrentUser,
      }
    )
  )
}

export const doFetchFoldersForDataTable = () => dispatch => {
  const queryId = LOAD_ALL_FOLDERS_QUERYID
  const cursor = 'all'

  return dispatch(
    doGraphqlRequest(
      FETCH_FOLDERS_V2,
      dataTableQuery(),
      {
        size: 1000,
      },
      {
        app: true,
        normalizationSchema: folderGraphQlV2ResponseSchema,
        searches: {
          queryId,
          cursor,
          extractPagination: 'gqlv2',
        },
        transformResponse: calculateFolderAccess,
      }
    )
  )
}

export const doFetchFoldersInMemory = createDoFetchInMemoryByQueryId({
  fromQueryId: LOAD_ALL_FOLDERS_QUERYID,
  entityType: 'folder',
  doLoadAllFn: doFetchFoldersForDataTable,
})

export function createFetchFoldersRequestAction() {
  return { type: FETCH_FOLDERS_REQUEST }
}

export function createFetchFoldersSuccessAction(res, { getState }) {
  const state = getState()
  const currentUser = selectCurrentUser(state)
  const data = res.json.data
  return { type: FETCH_FOLDERS_SUCCESS, data: { ...data, currentUser } }
}

export function createFetchFoldersFailAction(error) {
  return { type: FETCH_FOLDERS_FAIL, data: { err: error } }
}

export function createFetchFoldersLoader() {
  return new BootstrapLoader(
    'folders',
    createFetchFoldersRequestAction,
    createFetchFoldersSuccessAction,
    createFetchFoldersFailAction,
    getState => {
      return graphql(oauthTokenSelector(getState()), collectionQueryV1())
    }
  )
}
// eslint-disable-next-line no-unused-vars
export const doDeleteSingleFolder = (id, options = {}) => dispatch => {
  return dispatch(
    doApiWriteRequest(
      DELETE_FOLDER,
      `api/settings/folders/${id}`,
      {},
      {
        method: 'DELETE',
        optimist: {},
        normalizationSchema: folderGraphQlResponseSchema,
        moduleOptions: {
          entities: {
            targetOperation: 'remove',
            additionalActions: [
              {
                // Because we're passing through a global id here, we need to manually delete
                // the entity from the store
                entityType: 'folder',
                entityId: id,
                stores: ['pending', 'current'],
                operation: 'remove',
                phases: ['STARTED'],
              },
            ],
          },
        },
      }
    )
  )
}

export const doDeleteFolders = (ids, options = {}) => dispatch => {
  // Promise.all is not technically required in the current implementation because
  // we'll only step into this block if the ids array has a single id, but I do
  // want to leave the door open for us to say that deleting less than X rows uses
  // single deletes instead of batch deletes
  return Promise.all(ids.map(id => dispatch(doDeleteSingleFolder(id, options))))
}

export const createFetchFolders = {
  createFetchFoldersRequestAction,
  createFetchFoldersSuccessAction,
  createFetchFoldersFailAction,
  createFetchFoldersLoader,
}
